// ZUSTAND→ATOM off-ramp (docs/projects/complete/web/zustand-to-effect-atom-migration.md)
// Status: converted
// atomKvsSync persisted atom (schema-validated); zustand `{state, version}`
// envelopes migrate forward through the schema transform below.
import { useAtomSet, useAtomValue } from '@effect/atom-react';
import { Clock, Schema, SchemaTransformation, Struct } from 'effect';
import { useMemo } from 'react';
import {
  GOOGLE_CLICK_KEYS,
  UPLOADABLE_GOOGLE_CLICK_KEYS,
} from '@listening/api-contract/schemas';
import { atomKvsSync, fieldwiseKvsFallback } from '../atoms/kvs';
import { useAppRuntime } from '../query/runtime-context';

// Note: the Facebook `_fbc`/`_fbp` cookies are NOT tracked here — they are
// cookies (not URL params) and are read fresh at checkout time. See
// analytics/facebook-cookies.ts.
export type TrackingParamKey =
  | 'google_click_id'
  | 'google_wbraid'
  | 'google_gbraid'
  | 'facebook_click_id'
  | 'utm_source'
  | 'utm_medium'
  | 'utm_campaign'
  | 'utm_term'
  | 'utm_content';

const GOOGLE_CLICK_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

const timestampedValueSchema = Schema.Struct({
  value: Schema.String,
  timestamp: Schema.Int,
});
const optionalTimestampedValue = Schema.optional(timestampedValueSchema);

const paramsSchema = Schema.Struct({
  google_click_id: optionalTimestampedValue,
  google_wbraid: optionalTimestampedValue,
  google_gbraid: optionalTimestampedValue,
  facebook_click_id: optionalTimestampedValue,
  utm_source: optionalTimestampedValue,
  utm_medium: optionalTimestampedValue,
  utm_campaign: optionalTimestampedValue,
  utm_term: optionalTimestampedValue,
  utm_content: optionalTimestampedValue,
});

const stateSchema = Schema.Struct({ params: paramsSchema });

type TrackingParamsState = typeof stateSchema.Type;
export type TrackingParams = TrackingParamsState['params'];

const defaults: TrackingParamsState = { params: {} };

// zustand-persist wrote a `{state, version}` envelope under the same key
// ('tracking-store-state', kept from the @listening/shared era). Decode
// merges over defaults; encode writes the bare shape. See plan doc §C2.
const partialStateSchema = stateSchema.mapFields(
  Struct.map(Schema.optionalKey),
);
const persistedSchema = Schema.Union([
  Schema.Struct({ state: partialStateSchema, version: Schema.Int }),
  partialStateSchema,
]).pipe(
  Schema.decodeTo(
    stateSchema,
    SchemaTransformation.transform({
      decode: (stored) => ({
        ...defaults,
        ...('state' in stored ? stored.state : stored),
      }),
      encode: (state) => state,
    }),
  ),
);

// Exported for tests (migration coverage — this store feeds checkout ad
// attribution) and imperative access.
export const trackingParamsAtom = atomKvsSync({
  key: 'tracking-store-state',
  schema: persistedSchema,
  defaultValue: () => defaults,
  // Field-wise fallback for parity with the other preference stores. `params`
  // is the ONLY top-level field, so recovery here is effectively all-or-nothing
  // (an invalid params blob still degrades to defaults — there is no sibling
  // slice to salvage); it stays wired so added top-level fields recover for free.
  fieldFallback: fieldwiseKvsFallback({
    schema: stateSchema,
    defaultValue: () => defaults,
  }),
});

export type GoogleClickIds = {
  googleClickId: string | undefined;
  googleWbraid: string | undefined;
  googleGbraid: string | undefined;
};

export const mergeTrackingParams = (
  current: TrackingParams,
  params: Partial<Record<TrackingParamKey, string>>,
  now: number,
): TrackingParams => ({
  ...(UPLOADABLE_GOOGLE_CLICK_KEYS.some((key) => params[key])
    ? Struct.omit(current, GOOGLE_CLICK_KEYS)
    : current),
  ...Object.fromEntries(
    Object.entries(params).flatMap(([key, value]) =>
      value ? [[key, { value, timestamp: now }]] : [],
    ),
  ),
});

export type TrackingParamsStoreState = {
  params: TrackingParams;
  setTrackingParams: (
    params: Partial<Record<TrackingParamKey, string>>,
  ) => void;
  clearParam: (param: TrackingParamKey) => void;
  getValidatedGoogleClickIds: () => GoogleClickIds;
};

export function useTrackingParamsStore<T>(
  selector: (store: TrackingParamsStoreState) => T,
): T {
  // Time comes from the runtime's Clock (not Date.now) so tests can drive
  // gclid expiry with a TestClock-layered runtime.
  const runtime = useAppRuntime();
  const setState = useAtomSet(trackingParamsAtom);
  const actions = useMemo(
    () => ({
      setTrackingParams: (
        params: Partial<Record<TrackingParamKey, string>>,
      ) => {
        const now = runtime.runSync(Clock.currentTimeMillis);
        setState((current) => ({
          params: mergeTrackingParams(current.params, params, now),
        }));
      },
      clearParam: (param: TrackingParamKey) => {
        setState((current) => {
          const { [param]: _removed, ...params } = current.params;
          return { params };
        });
      },
    }),
    [runtime, setState],
  );
  // Derived-atom routing: gated on the selected value (zustand parity).
  return useAtomValue(trackingParamsAtom, (state) =>
    selector({
      params: state.params,
      ...actions,
      // Closes over the latest emitted state: called from event handlers,
      // where that value is current. Expiry also clears the stored param.
      getValidatedGoogleClickIds: () => {
        const now = runtime.runSync(Clock.currentTimeMillis);
        const read = (key: TrackingParamKey) => {
          const stored = state.params[key];
          if (!stored) return undefined;
          if (now - stored.timestamp <= GOOGLE_CLICK_MAX_AGE_MS) {
            return stored.value;
          }
          setState((current) => {
            const { [key]: _removed, ...params } = current.params;
            return { params };
          });
          return undefined;
        };

        return {
          googleClickId: read('google_click_id'),
          googleWbraid: read('google_wbraid'),
          googleGbraid: read('google_gbraid'),
        };
      },
    }),
  );
}

// Temporary alias for easier migration from @listening/shared.
export const useTrackingStore = useTrackingParamsStore; // knip
